/**
 * TradingView Study Name Map
 *
 * Maps shorthand indicator names (used in backend responses) to
 * TradingView's internal study names for createStudy().
 * Shared between TradingViewChart (datapack charts) and AssetChart (live charts).
 */

export const studyNameMap: Record<string, string> = {
  // Original 7 indicators
  'MACD': 'MACD',
  'RSI': 'Relative Strength Index',
  'BB': 'Bollinger Bands',
  'Stoch': 'Stochastic',
  'ATR': 'Average True Range',
  'ADX': 'Average Directional Index',
  'SuperTrend': 'Supertrend',

  // Batch 1: 10 indicators
  'EMA': 'Moving Average Exponential',
  'SMA': 'Moving Average',
  'ParabolicSAR': 'Parabolic SAR',
  'Ichimoku': 'Ichimoku Cloud',
  'WilliamsR': 'Williams %R',
  'CCI': 'Commodity Channel Index',
  'MFI': 'Money Flow Index',
  'OBV': 'On Balance Volume',
  'VWAP': 'VWAP',
  'KeltnerChannels': 'Keltner Channels',

  // Batch 2: 10 indicators
  'ROC': 'Rate Of Change',
  'Momentum': 'Momentum',
  'TRIX': 'TRIX',
  'UltimateOscillator': 'Ultimate Oscillator',
  'AccumulationDistribution': 'Accumulation/Distribution',
  'ChaikinMoneyFlow': 'Chaikin Money Flow',
  'Aroon': 'Aroon',
  'DMI': 'Directional Movement Index',
  'BalanceOfPower': 'Balance of Power',
  'PPO': 'Percentage Price Oscillator',

  // Batch 3: 10 indicators
  'ParabolicSARExtended': 'Parabolic SAR',
  'AroonOscillator': 'Aroon Oscillator',
  'ChaikinOscillator': 'Chaikin Oscillator',
  'APO': 'Absolute Price Oscillator',
  'TEMA': 'Triple EMA',
  'DEMA': 'Double EMA',
  'WMA': 'Weighted MA',
  'KAMA': 'Adaptive Moving Average',
  'Midpoint': 'Midpoint',
  'HilbertTransform': 'Hilbert Transform',

  // Batch 4: 10 indicators
  'StochasticRSI': 'Stochastic RSI',
  'AwesomeOscillator': 'Awesome Oscillator',
  'DonchianChannels': 'Donchian Channels',
  'VWMA': 'VWMA',
  'HullMA': 'Hull MA',
  'FisherTransform': 'Fisher Transform',
  'StandardDeviation': 'Standard Deviation',
  'LinearRegression': 'Linear Regression',
  'BollingerBandsPercent': 'Bollinger Bands %B',
  'VolumeOscillator': 'Volume Oscillator',

  // Batch 5: 10 indicators
  'BollingerBandsWidth': 'Bollinger Bands Width',
  'ChaikinVolatility': 'Chaikin Volatility',
  'ChandeMomentumOscillator': 'Chande Momentum Oscillator',
  'ChoppinessIndex': 'Choppiness Index',
  'DetrendedPriceOscillator': 'Detrended Price Oscillator',
  'EaseOfMovement': 'Ease of Movement',
  'ElderForceIndex': 'Elder\'s Force Index',
  'KnowSureThing': 'Know Sure Thing',
  'LinearRegressionSlope': 'Linear Regression Slope',
  'RelativeVigorIndex': 'Relative Vigor Index',

  // Batch 6: 10 indicators
  'MassIndex': 'Mass Index',
  'TrueStrengthIndicator': 'True Strength Index',
  'PriceVolumeTrend': 'Price Volume Trend',
  'VortexIndicator': 'Vortex Indicator',
  'CoppockCurve': 'Coppock Curve',
  'CorrelationCoefficient': 'Correlation Coefficient',
  'HistoricalVolatility': 'Historical Volatility',
  'WilliamsAlligator': 'Williams Alligator',
  'PivotPointsStandard': 'Pivot Points Standard',
  'KlingerOscillator': 'Klinger Oscillator',

  // Batch 7: 9 indicators
  'AcceleratorOscillator': 'Accelerator Oscillator',
  'AccumulativeSwingIndex': 'Accumulative Swing Index',
  'ALMA': 'Arnaud Legoux Moving Average',
  'ConnorsRSI': 'Connors RSI',
  'McGinleyDynamic': 'McGinley Dynamic',
  'RelativeVolatilityIndex': 'Relative Volatility Index',
  'SMIErgodic': 'SMI Ergodic Indicator',
  'SmoothedMovingAverage': 'Smoothed Moving Average',
  'StandardError': 'Standard Error',

  // Batch 8: 10 indicators
  '52WeekHighLow': '52 Week High/Low',
  'AveragePrice': 'Average Price',
  'MedianPrice': 'Median Price',
  'TypicalPrice': 'Typical Price',
  'Envelopes': 'Envelopes',
  'PriceChannel': 'Price Channel',
  'LeastSquaresMovingAverage': 'Least Squares Moving Average',
  'NetVolume': 'Net Volume',
  'ChandeKrollStop': 'Chande Kroll Stop',
  'WilliamsFractal': 'Williams Fractal',

  // Batch 9: 8 indicators
  'Advance/Decline': 'Advance/Decline',
  'ChopZone': 'Chop Zone',
  'EMACross': 'EMA Cross',
  'MACross': 'MA Cross',
  'GuppyMultipleMovingAverage': 'Guppy Multiple Moving Average',
  'MovingAverageChannel': 'Moving Average Channel',
  'MovingAverageHamming': 'Moving Average Hamming',
  'RankCorrelationIndex': 'Rank Correlation Index',

  // Batch 10: 10 indicators
  'StandardErrorBands': 'Standard Error Bands',
  'TrendStrengthIndex': 'Trend Strength Index',
  'VolatilityClose-to-Close': 'Volatility Close-to-Close',
  'VolatilityO-H-L-C': 'Volatility O-H-L-C',
  'VolatilityIndex': 'Volatility Index',
  'Volume': 'Volume',
  'ZigZag': 'Zig Zag',
  'MAwithEMACross': 'MA with EMA Cross',
  'MovingAverageMultiple': 'Moving Average Multiple',
};

/**
 * Apply studies to a TradingView chart from a comma-separated string.
 */
export function applyStudies(chart: { createStudy: (name: string, forceOverlay: boolean, lock: boolean) => void }, studiesStr: string): void {
  const studyList = studiesStr.split(',').map(s => s.trim()).filter(Boolean);

  for (const studyShortName of studyList) {
    const tvStudyName = studyNameMap[studyShortName];
    if (tvStudyName) {
      try {
        chart.createStudy(tvStudyName, false, false);
        console.log(`[TradingView] Applied: ${studyShortName} (${tvStudyName})`);
      } catch (error) {
        console.error(`[TradingView] Failed to apply ${studyShortName}:`, error);
      }
    } else {
      console.warn(`[TradingView] Unknown study: ${studyShortName}`);
    }
  }
}
